# Claims Intelligence APIs

CMS claims data APIs for billing validation, fee schedules, and coverage determination.

## Overview

The Claims Intelligence APIs provide programmatic access to CMS claims processing data. These APIs help billing teams, RCM platforms, and prior authorization systems validate claims before submission, reducing denials and rework.

All data is sourced from publicly available CMS datasets:

- **NCCI PTP Edits** — Correct Coding Initiative Procedure-to-Procedure edits
- **MUE Limits** — Medically Unlikely Edits (maximum units per service)
- **Physician Fee Schedule** — RVU values and Medicare payment calculations
- **Coverage Determinations** — Local Coverage Determination (LCD) policies

## Endpoints

| Method | Path | Description |
|--------|------|-------------|
| GET | `/v1/ncci/validate` | Validate whether two CPT/HCPCS codes can be billed together |
| GET | `/v1/mue/:hcpcs` | Look up MUE limits for a HCPCS code |
| POST | `/v1/mue/_batch` | Batch MUE lookup (up to 100 codes) |
| GET | `/v1/pfs/:hcpcs` | Look up Physician Fee Schedule / RVU data |
| POST | `/v1/pfs/_batch` | Batch PFS/RVU lookup (up to 100 codes) |
| GET | `/v1/coverage/check` | Check LCD coverage determinations for a HCPCS code |

## Use Cases

- **Pre-submission validation** — Check NCCI edits and MUE limits before submitting claims
- **Fee schedule lookups** — Get RVU breakdowns and Medicare payment amounts for procedure codes
- **Coverage checks** — Verify whether a procedure has active LCD policies that may require additional documentation
- **Batch processing** — Validate entire claim lines in a single request using batch endpoints

## Data Sources

| Dataset | Source | Update Frequency |
|---------|--------|-----------------|
| NCCI PTP Edits | CMS National Correct Coding Initiative | Quarterly |
| MUE Values | CMS Medically Unlikely Edits | Quarterly |
| Physician Fee Schedule | CMS PFS/RVU Files | Annually (with quarterly updates) |
| Coverage Determinations | CMS Medicare Coverage Database | As published |

All datasets are **public domain** — no licensing restrictions on use.

## Required Scopes

- `claims.read` — All claims intelligence endpoints

## Pages in This Section

- [NCCI Edit Validation](/docs/claims/ncci) — Check if code pairs can be billed together
- [MUE Limits](/docs/claims/mue) — Look up maximum units per service
- [Physician Fee Schedule](/docs/claims/pfs) — RVU values and payment calculations
- [Coverage Determinations](/docs/claims/coverage) — LCD policy lookups
